import { Component, Input, model, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { DocRow } from '../libraries.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { NgForOf, NgIf } from '@angular/common';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { DocRowNode } from '../../models/MaterialTable';
import { TreeNodeComponent } from './tree-node.component';

@Component({
  selector: 'app-tree-table',
  standalone: true,
  imports: [
    MatTable,
    MatColumnDef,
    MatCheckbox,
    MatCell,
    MatRow,
    MatHeaderCell,
    MatHeaderRow,
    MatIcon,
    MatCellDef,
    NgForOf,
    MatRowDef,
    MatHeaderCellDef,
    MatHeaderRowDef,
    NgIf,
    TreeNodeComponent
  ],
  templateUrl: './tree-table.component.html',
  styleUrl: './tree-table.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class TreeTableComponent implements OnChanges, OnInit, OnDestroy {
  @Input() docs: DocRow[] = [];

  displayedColumns = ['state', 'entity', 'proceeding', 'title'];
  dataSource: DocRowNode[] = [];
  selection = new SelectionModel<DocRow>(true, []);
  docsSelected: DocRow[] = [];
  docsSelected$ = model<DocRow[]>([]);

  // Column widths (percentages)
  columnWidths = {
    state: 15,
    entity: 25,
    proceeding: 25,
    title: 35
  };

  private isResizing = false;
  private currentResizeColumn = '';
  private startX = 0;
  private startWidth = 0;
  private tableWidth = 0;

  ngOnInit(): void {
    // Build tree on init if docs are available
    if (this.docs && this.docs.length > 0) {
      this.dataSource = this.buildTree(this.docs);
      console.log('Initial tree data:', this.dataSource);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['docs'] && changes['docs'].currentValue) {
      this.dataSource = this.buildTree(changes['docs'].currentValue);
    }
  }

  buildTree(data: DocRow[]): DocRowNode[] {
    const map = new Map<string, DocRowNode>();

    for (const item of data) {
      const { state, company, proceeding, title } = item;

      if (!state || !company) continue;

      // Level 1: State
      if (!map.has(state)) {
        map.set(state, new DocRowNode(state, 1, true));
      }
      const stateNode = map.get(state)!;

      // Level 2: Company
      let companyNode = stateNode.children.find(n => n.name === company);
      if (!companyNode) {
        companyNode = new DocRowNode(company, 2, true);
        stateNode.children.push(companyNode);
      }

      // Level 3: Proceeding (optional)
      const proceedingNumber = proceeding?.number?.trim() || null;
      let parentNode = companyNode;

      if (proceedingNumber) {
        let proceedingNode = companyNode.children.find(n => n.name === proceedingNumber);
        if (!proceedingNode) {
          proceedingNode = new DocRowNode(proceedingNumber, 3, true);
          companyNode.children.push(proceedingNode);
        }
        parentNode = proceedingNode;
      }

      // Level 4: Document (or level 3 if no proceeding)
      const documentNode = new DocRowNode(title, proceedingNumber ? 4 : 3, false, item);
      parentNode.children.push(documentNode);
    }

    return Array.from(map.values());
  }

  toggleNode(node: DocRowNode) {
    if (node.expandable) {
      node.expanded = !node.expanded;
    }
  }

  toggleSelection(doc: DocRow) {
    this.selection.toggle(doc);

    // Update docsSelected array
    if (this.selection.isSelected(doc)) {
      // Add doc if not already in array
      if (!this.docsSelected.find(d => d.documentId === doc.documentId)) {
        this.docsSelected.push(doc);
      }
    } else {
      // Remove doc from array
      this.docsSelected = this.docsSelected.filter(d => d.documentId !== doc.documentId);
    }

    this.docsSelected$.set(this.docsSelected);
    console.log('docsSelected:', this.docsSelected$());
  }

  isSelected(doc: DocRow): boolean {
    return this.selection.isSelected(doc);
  }

  // Get all documents from a node recursively
  getAllDocs(node: DocRowNode): DocRow[] {
    const docs: DocRow[] = [];

    if (node.doc) {
      docs.push(node.doc);
    }

    node.children.forEach(child => {
      docs.push(...this.getAllDocs(child));
    });
    return docs;
  }

  toggleNodeSelection(node: DocRowNode) {
    const docs = this.getAllDocs(node);
    console.log('Selected', docs);

    if (this.isNodeSelected(node)) {
      // Deselect all docs in this node
      console.log('Deselect node', node);
      docs.forEach(doc => {
        this.selection.deselect(doc);
        // Remove from docsSelected array
        this.docsSelected = this.docsSelected.filter(d => d.documentId !== doc.documentId);
      });
    } else {
      // Select all docs in this node
      console.log('Select node', node);
      docs.forEach(doc => {
        this.selection.select(doc);
        // Add to docsSelected array if not already present
        if (!this.docsSelected.find(d => d.documentId === doc.documentId)) {
          this.docsSelected.push(doc);
        }
      });
    }
    this.docsSelected$.set(this.docsSelected);
    console.log('docsSelected:', this.docsSelected$());
  }

  searchForDoc(title: string): DocRow {
    return this.docs.find(x => x.title.toLowerCase().includes(title.toLowerCase()))!;
  }

  isNodeSelected(node: DocRowNode): boolean {
    const docs = this.getAllDocs(node);
    return docs.length > 0 && docs.every(doc => this.selection.isSelected(doc));
  }

  isNodePartiallySelected(node: DocRowNode): boolean {
    const docs = this.getAllDocs(node);
    const selectedCount = docs.filter(doc => this.selection.isSelected(doc)).length;
    return selectedCount > 0 && selectedCount < docs.length;
  }

  toggleAllStates(event: MatCheckboxChange) {
    if (event.checked) {
      // Select all documents in all nodes
      this.dataSource.forEach(node => {
        const docs = this.getAllDocs(node);
        docs.forEach(doc => {
          this.selection.select(doc);
          // Add to docsSelected array if not already present
          if (!this.docsSelected.find(d => d.documentId === doc.documentId)) {
            this.docsSelected.push(doc);
          }
        });
      });
    } else {
      // Deselect all
      this.selection.clear();
      // Clear docsSelected array
      this.docsSelected = [];
    }

    console.log('docsSelected:', this.docsSelected);
  }

  isAllSelected(): boolean {
    let totalDocs = 0;
    this.dataSource.forEach(node => {
      totalDocs += this.getAllDocs(node).length;
    });
    return totalDocs > 0 && this.selection.selected.length === totalDocs;
  }

  isSomeSelected(): boolean {
    let totalDocs = 0;
    this.dataSource.forEach(node => {
      totalDocs += this.getAllDocs(node).length;
    });
    return this.selection.selected.length > 0 && this.selection.selected.length < totalDocs;
  }

  getNodeCount(node: DocRowNode): number {
    return this.getAllDocs(node).length;
  }

  getNodeLabel(node: DocRowNode): string {
    switch (node.level) {
      case 1:
        return `State: ${node.name}`;
      case 2:
        return `Company: ${node.name}`;
      case 3:
        return node.expandable ? `Proceeding Number: ${node.name}` : node.name;
      case 4:
        return node.name;
      default:
        return node.name;
    }
  }

  getMockData(): DocRow[] {
    return [
      {
        state: 'IL',
        utility: 'Commonwealth Edison',
        filingType: 'Environmental',
        proceeding: { number: 'Docket 23-0285' },
        ingestedAt: '2024-01-24T12:25:00Z',
        title: 'ComEd Clean Energy Implementation Plan 2024',
        summary:
          'Strategic roadmap for renewable energy integration, carbon reduction initiatives, and clean energy infrastructure development across the Illinois service territory',
        filingDate: '2023-07-18',
        documentId: 'doc#comed-clean-energy-2024.pdf',
        libraryId: 'library#il-comed-library',
        fileId: 's3://drift-dev-document-store/libraries/il-comed-library/comed-clean-energy-2024.pdf',
        company: 'Commonwealth Edison',
        entity: 'Entity Exmple',
        year: 2023
      },
      {
        state: 'IL',
        utility: 'Commonwealth Edison',
        filingType: 'Service Quality',
        proceeding: { number: 'Docket 23-0301' },
        ingestedAt: '2024-01-23T15:40:00Z',
        title: 'ComEd Service Quality and Reliability Report 2023',
        summary:
          'Comprehensive annual report detailing system reliability metrics, outage performance statistics, customer service quality measures, and improvement initiatives',
        filingDate: '2023-11-30',
        documentId: 'doc#comed-service-quality-2023.pdf',
        libraryId: 'library#il-comed-library',
        fileId: 's3://drift-dev-document-store/libraries/il-comed-library/comed-service-quality-2023.pdf',
        company: 'Commonwealth Edison',
        year: 2023
      },
      {
        state: 'FL',
        utility: 'Florida Power & Light',
        filingType: 'Rate Schedule',
        proceeding: { number: 'Docket 20230067-EI' },
        ingestedAt: '2024-01-21T13:10:00Z',
        title: 'FPL Standard Offer Contract Rate Schedule 2023',
        summary:
          'Comprehensive update to rate schedules for residential, commercial, and industrial customers including time-of-use rates and demand charges',
        filingDate: '2023-08-25',
        documentId: 'doc#fpl-rate-schedule-2023.pdf',
        libraryId: 'library#fl-fpl-library',
        fileId: 's3://drift-dev-document-store/libraries/fl-fpl-library/fpl-rate-schedule-2023.pdf',
        company: 'Florida Power & Light',
        year: 2023
      },
      {
        state: 'CA',
        utility: 'Pacific Gas & Electric',
        filingType: 'Rate Case',
        proceeding: { number: '' },
        ingestedAt: '2024-01-15T10:00:00Z',
        title: 'Test Document 1',
        summary: 'First test document for cloning',
        filingDate: '2024-01-15',
        documentId: 'document#doc-001',
        libraryId: 'library#test-lib-123',
        fileId: 's3://drift-av-document-store/libraries/test-lib-123/test-doc-1.pdf',
        company: 'Pacific Gas & Electric',
        year: 2024
      },
      {
        state: 'CA',
        utility: 'Test Company',
        filingType: 'Test Case',
        proceeding: { number: '' },
        ingestedAt: '2024-01-15T10:00:00Z',
        title: 'Test Document 2',
        summary: 'Second test document for cloning',
        filingDate: '2024-01-15',
        documentId: 'document#doc-002',
        libraryId: 'library#test-lib-123',
        fileId: 's3://drift-av-document-store/libraries/test-lib-123/test-doc-2.pdf',
        company: 'Test Company',
        year: 2024
      },
      {
        state: 'CA',
        utility: 'Test Doc Company',
        filingType: 'Test Doc Type',
        proceeding: { number: '' },
        ingestedAt: '2024-01-15T10:00:00Z',
        title: 'Testing Document 3',
        summary: 'Third test document for cloning',
        filingDate: '2024-01-15',
        documentId: 'document#doc-003',
        libraryId: 'library#test-lib-123',
        fileId: 's3://drift-av-document-store/libraries/test-lib-123/test-doc-3.pdf',
        company: 'Test Doc Company',
        year: 2024
      },
      {
        state: 'CA',
        utility: 'Pacific Gas & Electric',
        filingType: 'Rate Case',
        proceeding: { number: '' },
        ingestedAt: '2024-01-15T10:00:00Z',
        title: '',
        summary: 'Test library with documents',
        filingDate: '',
        documentId: 'library#test-lib-123',
        libraryId: 'library#test-lib-123',
        fileId: '',
        company: 'Pacific Gas & Electric',
        year: 2024
      },
      {
        state: 'TX',
        utility: 'Electric Reliability Council of Texas',
        filingType: 'Rate Schedule',
        proceeding: { number: 'Docket 52373' },
        ingestedAt: '2024-01-17T14:22:00Z',
        title: 'ERCOT Wholesale Market Rate Schedule Update 2023',
        summary:
          'Comprehensive update to rate schedules governing wholesale electricity market operations, including energy, capacity, and ancillary service pricing',
        filingDate: '2023-03-20',
        documentId: 'doc#ercot-rate-schedule-2023.pdf',
        libraryId: 'library#tx-ercot-library',
        fileId: 's3://drift-dev-document-store/libraries/tx-ercot-library/ercot-rate-schedule-2023.pdf',
        company: 'Electric Reliability Council of Texas',
        year: 2023
      },
      {
        state: 'TX',
        utility: 'Electric Reliability Council of Texas',
        filingType: 'Service Quality',
        proceeding: { number: 'Docket 52401' },
        ingestedAt: '2024-01-18T11:45:00Z',
        title: 'ERCOT 2023 Annual Service Quality and Reliability Report',
        summary:
          'Comprehensive analysis of grid reliability performance, outage statistics, frequency response, and overall service quality metrics for the Texas interconnected system',
        filingDate: '2023-12-31',
        documentId: 'doc#ercot-service-quality-2023.pdf',
        libraryId: 'library#tx-ercot-library',
        fileId: 's3://drift-dev-document-store/libraries/tx-ercot-library/ercot-service-quality-2023.pdf',
        company: 'Electric Reliability Council of Texas',
        year: 2023
      },
      {
        state: 'CA',
        utility: 'Pacific Gas & Electric',
        filingType: 'General Rate Case',
        proceeding: { number: 'A.21-06-021' },
        ingestedAt: '2024-01-15T10:30:00Z',
        title: 'PG&E 2023 General Rate Case Application',
        summary:
          'Comprehensive application requesting rate increases for electric and gas services to support infrastructure investments and operational improvements',
        filingDate: '2021-06-30',
        documentId: 'doc#pge-general-rate-case-2023.pdf',
        libraryId: 'library#ca-pge-library',
        fileId: 's3://drift-dev-document-store/libraries/ca-pge-library/pge-general-rate-case-2023.pdf',
        company: 'Pacific Gas & Electric',
        year: 2021
      },
      {
        state: 'CA',
        utility: 'Pacific Gas & Electric',
        filingType: 'Grid Modernization',
        proceeding: { number: 'A.22-05-015' },
        ingestedAt: '2024-01-16T09:15:00Z',
        title: 'PG&E Grid Modernization Business Plan 2023-2026',
        summary:
          'Four-year strategic plan for modernizing electrical grid infrastructure, implementing smart grid technologies, and enhancing system reliability',
        filingDate: '2022-05-15',
        documentId: 'doc#pge-grid-modernization-2023.pdf',
        libraryId: 'library#ca-pge-library',
        fileId: 's3://drift-dev-document-store/libraries/ca-pge-library/pge-grid-modernization-2023.pdf',
        company: 'Pacific Gas & Electric',
        year: 2022
      }
    ];
  }

  // Start resizing
  onResizeStart(event: MouseEvent, column: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.currentResizeColumn = column;
    this.startX = event.pageX;
    this.startWidth = this.columnWidths[column as keyof typeof this.columnWidths];

    // Get table width for percentage calculations
    const table = (event.target as HTMLElement).closest('table');
    this.tableWidth = table?.offsetWidth || 1000;

    document.addEventListener('mousemove', this.onResize);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  // During resize
  onResize = (event: MouseEvent): void => {
    if (!this.isResizing) return;

    const diff = event.pageX - this.startX;
    const diffPercent = (diff / this.tableWidth) * 100;

    // Min 5%, Max 80%
    // Update the current column width
    this.columnWidths[this.currentResizeColumn as keyof typeof this.columnWidths] = Math.max(
      5,
      Math.min(80, this.startWidth + diffPercent)
    );
  };

  // End resizing
  onResizeEnd = (): void => {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.onResizeEnd);
  };

  // Get width style for a column
  getColumnWidth(column: string): string {
    return `${this.columnWidths[column as keyof typeof this.columnWidths]}%`;
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.onResizeEnd);
  }
}
